import { useState, useEffect } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  LayoutDashboard, Users, BookOpen, Truck, Calculator, Wallet, MessageSquare, Settings, ShieldCheck, BadgeCheck, Landmark, Wrench, BarChart3, BookOpenText, Banknote, Users2,
  Menu, X, ChevronDown, ChevronRight, LogOut, Bell, UserCircle, Receipt, FileText, AlertTriangle, Home, UserCog, Shield,
} from "lucide-react";

// ===== Menús =====
// Cada item lleva `requires` con el permiso RBAC mínimo (usualmente `{modulo}.view`).
// Si el usuario no lo tiene, el item NO aparece en el sidebar.
const funcionarioMenu = [
  { id: "dashboard", title: "Dashboard", icon: LayoutDashboard, path: "/dashboard", requires: "dashboard.view" },
  { id: "definiciones", title: "Definiciones", icon: BookOpen, path: "/definiciones", requires: "definiciones.view" },
  { id: "aseo-urbano", title: "Aseo Urbano", icon: Truck, path: "/aseo-urbano", requires: "aseo_urbano.view" },
  { id: "contribuyentes", title: "Contribuyentes", icon: Users, path: "/contribuyentes", requires: "contribuyentes.view" },
  { id: "liquidaciones", title: "Liquidaciones", icon: Calculator, path: "/liquidaciones", requires: "liquidaciones.view" },
  { id: "recaudaciones", title: "Recaudaciones", icon: Wallet, path: "/recaudaciones", requires: "recaudaciones.view" },
  { id: "conciliacion", title: "Conciliación Bancaria", icon: Landmark, path: "/conciliacion", requires: "conciliacion.view" },
  { id: "solvencias", title: "Solvencias", icon: BadgeCheck, path: "/solvencias", requires: "solvencias.view" },
  { id: "reportes", title: "Reportes", icon: BarChart3, path: "/reportes", requires: "reportes.view", children: [
    { id: "rep-actividad", title: "Actividad · Morosidad", path: "/reportes/actividad", icon: BarChart3 },
    { id: "rep-cuentas",   title: "Cuentas Recaudadoras", path: "/reportes/cuentas",   icon: Banknote },
    { id: "rep-por-cobrar", title: "Cuentas por Cobrar",  path: "/reportes/por-cobrar", icon: Wallet },
    { id: "rep-canal",     title: "Taquilla vs Online",   path: "/reportes/canal",     icon: Users2 },
  ] },
  { id: "reclamos", title: "Reclamos", icon: MessageSquare, path: "/reclamos", requires: "reclamos.view" },
  { id: "configuracion", title: "Configuración", icon: Settings, path: "/configuracion", requires: "configuracion.view" },
  { id: "manuales", title: "Manuales", icon: BookOpenText, path: "/manuales", requires: "manuales.view" },
];
const adminExtra = { id: "admin-usuarios", title: "Usuarios Funcionarios", icon: ShieldCheck, path: "/admin/usuarios" };
const adminUsuariosPortal = { id: "admin-usuarios-contribuyentes", title: "Usuarios Contribuyentes", icon: UserCog, path: "/admin/usuarios-contribuyentes", requires: "usuarios_contribuyentes.view" };
const adminRoles  = { id: "admin-roles", title: "Roles y Permisos", icon: ShieldCheck, path: "/admin/roles" };
const adminAuditoria = { id: "admin-auditoria", title: "Auditoría", icon: Shield, path: "/admin/auditoria" };
const adminMantenimiento = { id: "admin-mantenimiento", title: "Mantenimiento", icon: Wrench, path: "/mantenimiento" };

const contribuyenteMenu = [
  { id: "portal-home", title: "Inicio", icon: Home, path: "/portal" },
  { id: "portal-deuda", title: "Mi Deuda", icon: AlertTriangle, path: "/portal/deuda" },
  { id: "portal-reportar", title: "Reportar Pago", icon: Receipt, path: "/portal/reportar-pago" },
  { id: "portal-historial", title: "Historial de Pagos", icon: FileText, path: "/portal/historial" },
  { id: "portal-solvencia", title: "Mi Solvencia", icon: BadgeCheck, path: "/portal/solvencia" },
  { id: "portal-reclamos", title: "Mis Reclamos", icon: MessageSquare, path: "/portal/reclamos" },
  { id: "portal-manual", title: "Ayuda / Manual", icon: BookOpenText, path: "/portal/manual" },
];

export default function DashboardLayout({ mode = "funcionario" }) {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});

  const isContrib = mode === "contribuyente";
  // Filtrar items del funcionario por permiso RBAC (Dashboard siempre visible).
  let menu = isContrib
    ? contribuyenteMenu
    : funcionarioMenu.filter(item => !item.requires || hasPermission(item.requires));
  if (!isContrib && user?.role === "superadmin") menu = [...menu, adminExtra, adminUsuariosPortal, adminRoles, adminAuditoria, adminMantenimiento];
  else if (!isContrib && user?.role === "admin_financiero") menu = [...menu, adminMantenimiento];
  // Para roles RBAC (no superadmin ni admin_financiero legacy), agregar el módulo si tiene permiso
  else if (!isContrib && hasPermission(adminUsuariosPortal.requires)) menu = [...menu, adminUsuariosPortal];

  // Guard de ruta: si el usuario está en un módulo funcionario y no tiene el permiso 'view',
  // lo redirigimos al primer módulo disponible (o al dashboard si lo tiene).
  useEffect(() => {
    if (isContrib || !user || !Array.isArray(user.permissions)) return;
    if (user.role === "superadmin") return;
    const path = location.pathname;
    if (path.startsWith("/portal") || path.startsWith("/admin")) return;
    // Fallback: primer item del menú permitido (o /dashboard si lo tiene).
    const firstAllowed = menu[0]?.path || "/dashboard";
    // Si es /dashboard pero no tiene dashboard.view, ir al primer módulo permitido
    if ((path === "/dashboard" || path === "/") && !hasPermission("dashboard.view")) {
      if (firstAllowed !== "/dashboard") navigate(firstAllowed, { replace: true });
      return;
    }
    if (path === "/dashboard" || path === "/") return;
    const match = funcionarioMenu.find(m => m.path !== "/dashboard" && path.startsWith(m.path));
    if (match?.requires && !hasPermission(match.requires)) {
      navigate(firstAllowed, { replace: true });
    }
  }, [location.pathname, user?.role, user?.permissions?.length]);  // eslint-disable-line

  const isActive = (path) => location.pathname === path || (path !== "/dashboard" && path !== "/portal" && location.pathname.startsWith(path));

  const doLogout = () => { logout(); navigate("/"); };

  const Sidebar = () => (
    <>
      <div className="px-5 py-5 border-b border-white/10">
        <Link to={isContrib ? "/portal" : "/dashboard"} className="flex items-center gap-3" data-testid="sidebar-logo">
          <img src="/brand/icon_palmaplus.png" alt="Palma Plus" className="w-11 h-11 rounded-xl bg-white shadow-md object-contain p-0.5" style={{ boxShadow: "0 0 0 3px rgba(234,88,12,0.35)" }} />
          {sidebarOpen && (
            <div className="overflow-hidden">
              <h1 className="font-display font-extrabold text-white text-sm leading-tight">SISAUR</h1>
              <p className="text-white/55 text-[10px] uppercase tracking-wider">Saneamiento Urbano</p>
            </div>
          )}
        </Link>
      </div>

      {sidebarOpen && (
        <div className="px-5 pt-5 pb-2">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-orange-400/85">
            {isContrib ? "Mi Portal" : "Menú Principal"}
          </p>
        </div>
      )}

      <ScrollArea className="flex-1 px-2 py-2">
        <nav className="space-y-1">
          {menu.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const hasChildren = Array.isArray(item.children) && item.children.length > 0;
            const anyChildActive = hasChildren && item.children.some(ch => isActive(ch.path));
            const parentActive = active || anyChildActive;
            const isOpen = expandedMenus[item.id] ?? parentActive;

            if (hasChildren) {
              return (
                <div key={item.id}>
                  <button
                    type="button"
                    data-testid={`menu-${item.id}`}
                    onClick={() => setExpandedMenus(m => ({ ...m, [item.id]: !isOpen }))}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                      parentActive ? "menu-link-active" : "text-white/70 hover:bg-white/[0.06] hover:text-white"
                    }`}
                  >
                    <Icon className={`w-5 h-5 flex-shrink-0 ${parentActive ? "text-orange-400" : "text-white/55"}`} strokeWidth={1.7} />
                    {sidebarOpen && <span className="text-sm font-medium flex-1 text-left">{item.title}</span>}
                    {sidebarOpen && (isOpen ? <ChevronDown className="w-4 h-4 text-white/50" /> : <ChevronRight className="w-4 h-4 text-white/50" />)}
                  </button>
                  {sidebarOpen && isOpen && (
                    <div className="ml-5 mt-1 space-y-0.5 border-l border-white/10 pl-2">
                      {item.children.map(ch => {
                        const ChIcon = ch.icon;
                        const chActive = isActive(ch.path);
                        return (
                          <Link key={ch.id} to={ch.path} data-testid={`menu-${ch.id}`}
                            onClick={() => setMobileOpen(false)}
                            className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition ${
                              chActive ? "bg-orange-500/20 text-orange-200 font-semibold" : "text-white/60 hover:bg-white/[0.05] hover:text-white"
                            }`}>
                            {ChIcon && <ChIcon className="w-3.5 h-3.5" />}
                            <span>{ch.title}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <Link key={item.id} to={item.path} data-testid={`menu-${item.id}`}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                  active ? "menu-link-active" : "text-white/70 hover:bg-white/[0.06] hover:text-white"
                }`}>
                <Icon className={`w-5 h-5 flex-shrink-0 ${active ? "text-orange-400" : "text-white/55"}`} strokeWidth={1.7} />
                {sidebarOpen && <span className="text-sm font-medium">{item.title}</span>}
              </Link>
            );
          })}
        </nav>
      </ScrollArea>

      {sidebarOpen && user && (
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
              {user.nombre?.charAt(0)}{user.apellido?.charAt(0)}
            </div>
            <div className="overflow-hidden">
              <p className="text-white text-sm font-medium truncate">{user.nombre} {user.apellido}</p>
              <p className="text-white/55 text-[10px] uppercase tracking-wider">{user.role}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-[#F8FAF6] flex" data-testid="dashboard-layout">
      <aside className={`hidden lg:flex flex-col sidebar-bg fixed h-screen z-30 transition-all duration-300 ${sidebarOpen ? "w-64" : "w-20"}`} data-testid="sidebar">
        <Sidebar />
      </aside>

      {mobileOpen && <div className="lg:hidden fixed inset-0 bg-black/55 z-40" onClick={() => setMobileOpen(false)} />}
      <aside className={`lg:hidden fixed inset-y-0 left-0 w-64 sidebar-bg z-50 transform transition-transform duration-300 ${mobileOpen ? "translate-x-0" : "-translate-x-full"} flex flex-col`}>
        <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)} className="absolute top-3 right-3 text-white hover:bg-white/10">
          <X className="w-5 h-5" />
        </Button>
        <Sidebar />
      </aside>

      <div className={`flex-1 flex flex-col ${sidebarOpen ? "lg:ml-64" : "lg:ml-20"} transition-all duration-300`}>
        <div className="topbar-orange-strip h-1" />
        <header className="bg-white border-b border-green-100 sticky top-0 z-20 shadow-sm">
          <div className="flex items-center justify-between px-4 lg:px-6 py-3">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
              <Button variant="ghost" size="icon" className="hidden lg:flex" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="sidebar-toggle">
                <Menu className="w-5 h-5" />
              </Button>
              <div className="hidden md:flex items-center gap-2">
                <img src="/brand/icon_palmaplus.png" alt="Palma Plus" className="w-9 h-9 rounded-lg bg-white object-contain p-0.5" />
                <div className="leading-tight">
                  <p className="font-display font-bold text-green-900 text-sm">SISAUR</p>
                  <p className="text-[10px] text-green-700/60">Palma Plus · Saneamiento Urbano</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                <Bell className="w-5 h-5 text-green-700" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-orange-500 rounded-full" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                    <div className="w-8 h-8 bg-green-700 rounded-full flex items-center justify-center text-white text-xs font-bold">
                      {user?.nombre?.charAt(0)}{user?.apellido?.charAt(0)}
                    </div>
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium text-green-900">{user?.nombre}</p>
                      <p className="text-[10px] text-green-700/60 capitalize">{user?.role?.replace("_", " ")}</p>
                    </div>
                    <ChevronDown className="w-4 h-4 text-green-700/60" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Mi Cuenta</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(isContrib ? "/portal" : "/dashboard")} data-testid="menu-home-link">
                    <UserCircle className="w-4 h-4 mr-2" /> {isContrib ? "Mi Portal" : "Dashboard"}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={doLogout} className="text-red-600" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6" data-testid="main-content">
          <Outlet />
        </main>

        <footer className="bg-white border-t border-green-100 px-6 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-green-700/60">
            <p>© {new Date().getFullYear()} SISAUR · Sistema de Saneamiento Urbano</p>
            <p>Operado por Palma Plus · Por una ciudad más limpia</p>
          </div>
        </footer>
      </div>
    </div>
  );
}
